import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Avatar,
  Box,
  Grid,
  Paper,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { motion } from "framer-motion";
import { ContextData } from "../../../../context/Context";
import axios from "axios";
import baseUrl from "../../../../BaseURL";

// ✅ Custom Card and CardContent using MUI Paper
const Card = ({ children, sx }) => (
  <Paper
    elevation={3}
    sx={{
      borderRadius: 4,
      padding: 3,
      backgroundColor: "#f9f9f9",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const CardContent = ({ children }) => (
  <Box sx={{ paddingX: 1 }}>{children}</Box>
);

const WalletProfile = (props) => {
  const theme = useTheme();
  const { setLoader, accesstoken } = useContext(ContextData);

  const [Data, setData] = useState(null);
  const [TotalUsed, setTotalUsed] = useState(0);
  const [Balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function FetchData() {
      setLoading(true);
      try {
        const response = await axios.get(
          `${baseUrl}/auth/get_credit_record/${props.data.token}`,
          {
            withCredentials: true,
            headers: {
              Accept: "application/json",
              authorization: `Bearer ${accesstoken !== null && accesstoken}`,
            },
          }
        );
        if (!isMounted) return;
        setData(response.data);
        const T = response.data.reduce(
          (total, num) => total + num.paid_total,
          0
        );
        setTotalUsed(T);
      } catch (err) {
        if (err) console.log(err.message);
        setData([]);
      }
    }

    async function FetchAmountData() {
      try {
        const response = await axios.get(
          `${baseUrl}/auth/get_amount/${props.data.token}`,
          {
            withCredentials: true,
            headers: {
              Accept: "application/json",
              authorization: `Bearer ${accesstoken !== null && accesstoken}`,
            },
          }
        );
        if (!isMounted) return;
        setBalance(response.data[0]?.amount || 0);
      } catch (err) {
        if (err) console.log(err.message);
        setBalance(0);
      }
    }

    Promise.all([FetchData(), FetchAmountData()]).finally(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Box p={4} sx={{ backgroundColor: "#f0f0f0" }}>
      <Typography variant="h4" mb={2} fontWeight={600}>
        Wallet Overview
      </Typography>

      {/* Wallet Balance */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CardContent>
            <Typography variant="h6" color="text.secondary">
              Wallet Balance
            </Typography>
            <Typography variant="h3" color="primary" fontWeight={700}>
              Credit: {Balance}
            </Typography>
          </CardContent>
          <CardContent>
            <Typography variant="h6" color="text.secondary">
              Total Spent
            </Typography>
            <Typography variant="h3" color="secondary" fontWeight={700}>
              Used Credit: ₦{TotalUsed.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </motion.div>

      {/* Transactions */}
      <Typography variant="h5" mt={4} mb={2} fontWeight={600}>
        Recent Transactions
      </Typography>

      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={120}
        >
          <CircularProgress color="primary" />
          <Typography mt={2} color="text.secondary">
            Getting Transactions...
          </Typography>
        </Box>
      ) : Data && Data.length > 0 ? (
        <Grid container spacing={2}>
          {Data.map((tx) => (
            <Grid item xs={12} key={tx.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderRadius: 3,
                    backgroundColor: "green",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: "darkblue",
                        mr: 2,
                      }}
                    >
                      {tx.type === "credit" ? (
                        <ArrowDownward />
                      ) : (
                        <ArrowUpward />
                      )}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        <sub>User ID: {tx.ID}</sub>
                      </Typography>
                      <Typography variant="body2" color="white">
                        {`Credit to Registered:  ${tx.amount} members`}
                      </Typography>
                      <Typography variant="body2" color="white">
                        <sub>
                          Date:{" "}
                          {tx.createdAt.slice(
                            2,
                            tx.createdAt.lastIndexOf("T")
                          )}
                        </sub>{" "}
                        <sub>
                          Time:{" "}
                          {tx.createdAt.slice(
                            tx.createdAt.lastIndexOf("T") + 1
                          )}
                        </sub>
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color={"white"}
                  >
                    {`Paid amount:  ₦${tx.paid_amount.toLocaleString()} per member`}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color={"white"}
                  >
                    {`Total amount paid:  ₦${tx.paid_total.toLocaleString()}`}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={120}
        >
          <Typography color="text.secondary" fontWeight={500}>
            No Transactions record
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WalletProfile;